// src/components/templates/diary/DiarySettingsView.jsx
import React, { useState, useEffect } from 'react';
import { 
  User, Bell, Clock, Save, Shield, AlertTriangle, RotateCcw, Check,
  Palette, Calendar, Download, Upload, Trash2, Eye, EyeOff, Moon, Sun,
  Globe, Lock, Key, Smartphone, Mail, Volume2, VolumeX, Keyboard,
  ChevronDown, ChevronRight, HelpCircle, ExternalLink, Sparkles,
  Monitor, Laptop, FileText, Database, Mic, Camera, MapPin, Zap,
  Building2, Users, FolderTree
} from 'lucide-react';
import { Card, Button, Badge } from './UIComponents';
import { WorkspacesManager } from './WorkspacesManager';


// --- TOGGLE SWITCH COMPONENT ---
const ToggleSwitch = ({ checked, onChange, disabled }) => (
  <label className={`relative inline-flex items-center ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
    <input 
      type="checkbox" 
      className="sr-only peer"
      checked={checked}
      onChange={(e) => !disabled && onChange?.(e.target.checked)}
      disabled={disabled}
    />
    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600" />
  </label>
);

// --- SETTING ROW COMPONENT ---
const SettingRow = ({ icon: Icon, label, description, children, badge }) => (
  <div className="flex items-start justify-between py-4 border-b border-gray-100 last:border-0">
    <div className="flex items-start gap-3 flex-1">
      {Icon && <Icon className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">{label}</span>
          {badge && <Badge color="purple">{badge}</Badge>}
        </div>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
    </div>
    <div className="ml-4 shrink-0">{children}</div>
  </div>
);

// --- COLLAPSIBLE SECTION ---
const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = true, badge }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <Card className="overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-indigo-600" />}
          <span className="text-lg font-semibold text-gray-800">{title}</span>
          {badge && <Badge color="indigo">{badge}</Badge>}
        </div>
        {isOpen ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
      </button>
      {isOpen && <div className="px-4 pb-4 border-t border-gray-100">{children}</div>}
    </Card>
  );
};

// --- COLOR PICKER ---
const ColorPicker = ({ value, onChange, colors }) => (
  <div className="flex gap-2">
    {colors.map(color => (
      <button
        key={color}
        onClick={() => onChange(color)}
        className={`w-8 h-8 rounded-full border-2 transition-all ${
          value === color ? 'border-gray-800 scale-110 shadow-lg' : 'border-transparent hover:scale-105'
        }`}
        style={{ backgroundColor: color }}
      />
    ))}
  </div>
);

// --- SETTINGS TAB NAVIGATION ---
const SettingsTabNavigation = ({ activeTab, onTabChange }) => {
  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'workspaces', label: 'Workspaces', icon: Building2 },
    { id: 'preferences', label: 'Preferences', icon: Palette },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'ai', label: 'AI Features', icon: Sparkles },
    { id: 'data', label: 'Data', icon: Database }
  ];

  return (
    <div className="flex gap-1 bg-gray-100 p-1 rounded-xl overflow-x-auto">
      {tabs.map(tab => {
        const Icon = tab.icon;
        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              activeTab === tab.id
                ? 'bg-white text-indigo-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <Icon className="w-4 h-4" />
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
};

// --- MAIN COMPONENT ---
export const DiarySettingsView = ({ 
  settings = {}, 
  onSaveSettings, 
  onResetData,
  onExportData,
  onImportData,
  bookId,
  bookData = {},
  user,
  contacts = [],
  customStyling = {}
}) => {
  const defaultSettings = {
    // Profile
    userName: '',
    userRole: '',
    userEmail: '',
    userAvatar: '',
    // Preferences
    defaultMeetingDuration: 30,
    defaultView: 'DASHBOARD',
    weekStartsOn: 0,
    timeFormat: '12h',
    dateFormat: 'MM/DD/YYYY',
    // Notifications
    enableNotifications: true,
    notificationSound: true,
    emailReminders: false,
    reminderTime: 15,
    // Appearance
    theme: 'light',
    accentColor: '#6366f1',
    compactMode: false,
    showWeekNumbers: false,
    // Privacy
    showCompletedMeetings: true,
    defaultMeetingPrivacy: 'visible',
    requireConfirmDelete: true,
    // Working Hours
    enableWorkingHours: false,
    workingHoursStart: '09:00',
    workingHoursEnd: '17:00',
    workingDays: [1, 2, 3, 4, 5],
    // AI Features
    enableAI: false,
    aiProvider: 'gemini',
    aiApiKey: '',
    enableTranscription: false,
    enableSummaries: false,
    // Integrations
    enableVoiceRecording: true,
    enableLocationServices: false,
    // Active Workspace
    activeWorkspaceId: null,
    ...settings
  };

  const [formData, setFormData] = useState(defaultSettings);
  const [isSaved, setIsSaved] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');

  useEffect(() => {
    setFormData({ ...defaultSettings, ...settings });
  }, [settings]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setIsSaved(false);
    setHasChanges(true);
  };

  const handleSave = () => {
    onSaveSettings?.(formData);
    setIsSaved(true);
    setHasChanges(false);
    setTimeout(() => setIsSaved(false), 3000);
  };

  const handleExport = () => {
    if (onExportData) {
      onExportData();
    } else {
      const data = JSON.stringify(formData, null, 2);
      const blob = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `diary-settings-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const themeColors = ['#6366f1', '#8b5cf6', '#ec4899', '#ef4444', '#f59e0b', '#10b981', '#06b6d4', '#3b82f6'];

  const daysOfWeek = [
    { value: 0, label: 'Sun' },
    { value: 1, label: 'Mon' },
    { value: 2, label: 'Tue' },
    { value: 3, label: 'Wed' },
    { value: 4, label: 'Thu' },
    { value: 5, label: 'Fri' },
    { value: 6, label: 'Sat' },
  ];

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 shrink-0">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <User className="w-6 h-6 mr-2 text-indigo-600" />
              Settings
            </h1>
            <p className="text-sm text-gray-500 mt-1">Manage your profile, workspaces, and preferences</p>
          </div>
          <div className="flex items-center gap-3">
            {hasChanges && (
              <span className="text-sm text-amber-600 flex items-center">
                <AlertTriangle className="w-4 h-4 mr-1" /> Unsaved changes
              </span>
            )}
            <Button 
              onClick={handleSave} 
              className={isSaved ? 'bg-green-600 hover:bg-green-700' : ''}
              disabled={!hasChanges && !isSaved}
            >
              {isSaved ? <><Check className="w-4 h-4 mr-2" /> Saved</> : <><Save className="w-4 h-4 mr-2" /> Save Changes</>}
            </Button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mt-4">
          <SettingsTabNavigation activeTab={activeTab} onTabChange={setActiveTab} />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-3xl mx-auto space-y-6">
          
          {/* PROFILE TAB */}
          {activeTab === 'profile' && (
            <>
              <CollapsibleSection title="Profile Information" icon={User}>
                <div className="space-y-4 pt-4">
                  <div className="flex items-center gap-4 mb-6">
                    {/* Avatar */}
                    {user?.photoURL ? (
                      <img 
                        src={user.photoURL}
                        alt="Profile"
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                        style={{ backgroundColor: customStyling.primaryColor }}
                      >
                        {(user?.displayName?.charAt(0) || 'U').toUpperCase()}
                      </div>
                    )}
                    <div>
                      <h3 className="font-semibold text-gray-800">{user?.displayName || 'Your Name'}</h3>
                      <p className="text-sm text-gray-500">{formData.userRole || 'Your Role'}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                      <input 
                        type="text"
                        value={user?.displayName || formData.userName || ''}
                        onChange={(e) => handleChange('userName', e.target.value)}
                        placeholder="e.g. John Doe"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Role / Title</label>
                      <input 
                        type="text"
                        value={formData.userRole}
                        onChange={(e) => handleChange('userRole', e.target.value)}
                        placeholder="e.g. Product Manager"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                      <input 
                        type="email"
                        value={user?.email || ''}
                        disabled
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500"
                      />
                    </div>
                  </div>
                </div>
              </CollapsibleSection>

              {/* Working Hours */}
              <CollapsibleSection title="Working Hours" icon={Clock} defaultOpen={false}>
                <div className="pt-2">
                  <SettingRow icon={Clock} label="Enable Working Hours" description="Set your availability hours">
                    <ToggleSwitch checked={formData.enableWorkingHours} onChange={(v) => handleChange('enableWorkingHours', v)} />
                  </SettingRow>

                  {formData.enableWorkingHours && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Start Time</label>
                          <input
                            type="time"
                            value={formData.workingHoursStart}
                            onChange={(e) => handleChange('workingHoursStart', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">End Time</label>
                          <input
                            type="time"
                            value={formData.workingHoursEnd}
                            onChange={(e) => handleChange('workingHoursEnd', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-2">Working Days</label>
                        <div className="flex flex-wrap gap-2">
                          {daysOfWeek.map(day => (
                            <button
                              key={day.value}
                              onClick={() => {
                                const days = formData.workingDays.includes(day.value)
                                  ? formData.workingDays.filter(d => d !== day.value)
                                  : [...formData.workingDays, day.value];
                                handleChange('workingDays', days);
                              }}
                              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                                formData.workingDays.includes(day.value)
                                  ? 'bg-indigo-100 text-indigo-700'
                                  : 'bg-gray-100 text-gray-500'
                              }`}
                            >
                              {day.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CollapsibleSection>
            </>
          )}

          {/* WORKSPACES TAB */}
          {activeTab === 'workspaces' && (
            <Card className="p-6">
              <WorkspacesManager 
                bookId={bookId}
                user={user}
                contacts={contacts}
              />
            </Card>
          )}

          {/* PREFERENCES TAB */}
          {activeTab === 'preferences' && (
            <>
              {/* Appearance Section */}
              <CollapsibleSection title="Appearance" icon={Palette}>
                <div className="pt-2">
                  <SettingRow 
                    icon={formData.theme === 'dark' ? Moon : Sun} 
                    label="Theme" 
                    description="Choose your preferred color scheme"
                  >
                    <div className="flex bg-gray-100 p-1 rounded-lg">
                      {['light', 'dark', 'system'].map(theme => (
                        <button
                          key={theme}
                          onClick={() => handleChange('theme', theme)}
                          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all capitalize ${
                            formData.theme === theme ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          {theme}
                        </button>
                      ))}
                    </div>
                  </SettingRow>

                  <SettingRow icon={Palette} label="Accent Color" description="Primary color for buttons and highlights">
                    <ColorPicker 
                      value={formData.accentColor} 
                      onChange={(color) => handleChange('accentColor', color)}
                      colors={themeColors}
                    />
                  </SettingRow>

                  <SettingRow icon={Monitor} label="Compact Mode" description="Reduce spacing for more content">
                    <ToggleSwitch checked={formData.compactMode} onChange={(v) => handleChange('compactMode', v)} />
                  </SettingRow>

                  <SettingRow icon={Calendar} label="Show Week Numbers" description="Display week numbers in calendar">
                    <ToggleSwitch checked={formData.showWeekNumbers} onChange={(v) => handleChange('showWeekNumbers', v)} />
                  </SettingRow>
                </div>
              </CollapsibleSection>

              {/* Calendar Preferences */}
              <CollapsibleSection title="Calendar Preferences" icon={Calendar}>
                <div className="pt-2">
                  <SettingRow icon={Clock} label="Default Meeting Duration" description="Pre-selected duration for new meetings">
                    <select 
                      className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={formData.defaultMeetingDuration}
                      onChange={(e) => handleChange('defaultMeetingDuration', parseInt(e.target.value))}
                    >
                      <option value={15}>15 minutes</option>
                      <option value={30}>30 minutes</option>
                      <option value={45}>45 minutes</option>
                      <option value={60}>1 hour</option>
                      <option value={90}>1.5 hours</option>
                      <option value={120}>2 hours</option>
                    </select>
                  </SettingRow>

                  <SettingRow icon={Calendar} label="Week Starts On" description="First day of your week">
                    <select 
                      className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={formData.weekStartsOn}
                      onChange={(e) => handleChange('weekStartsOn', parseInt(e.target.value))}
                    >
                      <option value={0}>Sunday</option>
                      <option value={1}>Monday</option>
                      <option value={6}>Saturday</option>
                    </select>
                  </SettingRow>

                  <SettingRow icon={Clock} label="Time Format" description="12-hour or 24-hour format">
                    <div className="flex bg-gray-100 p-1 rounded-lg">
                      {['12h', '24h'].map(format => (
                        <button
                          key={format}
                          onClick={() => handleChange('timeFormat', format)}
                          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                            formData.timeFormat === format ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          {format}
                        </button>
                      ))}
                    </div>
                  </SettingRow>

                  <SettingRow icon={Globe} label="Default View" description="Starting view when opening diary">
                    <select 
                      className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={formData.defaultView}
                      onChange={(e) => handleChange('defaultView', e.target.value)}
                    >
                      <option value="DASHBOARD">Dashboard</option>
                      <option value="CALENDAR">Calendar</option>
                      <option value="PLANNER">Planner</option>
                      <option value="MEETINGS">Meetings</option>
                      <option value="JOURNAL">Journal</option>
                    </select>
                  </SettingRow>
                </div>
              </CollapsibleSection>

              {/* Privacy */}
              <CollapsibleSection title="Privacy & Security" icon={Shield} defaultOpen={false}>
                <div className="pt-2">
                  <SettingRow icon={Eye} label="Show Completed Meetings" description="Display completed meetings in calendar">
                    <ToggleSwitch checked={formData.showCompletedMeetings} onChange={(v) => handleChange('showCompletedMeetings', v)} />
                  </SettingRow>

                  <SettingRow icon={Lock} label="Default Meeting Privacy" description="Default visibility for new meetings">
                    <select 
                      className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={formData.defaultMeetingPrivacy}
                      onChange={(e) => handleChange('defaultMeetingPrivacy', e.target.value)}
                    >
                      <option value="private">Private</option>
                      <option value="workspace">Workspace</option>
                      <option value="visible">Visible</option>
                    </select>
                  </SettingRow>

                  <SettingRow icon={AlertTriangle} label="Confirm Before Delete" description="Require confirmation for deletions">
                    <ToggleSwitch checked={formData.requireConfirmDelete} onChange={(v) => handleChange('requireConfirmDelete', v)} />
                  </SettingRow>
                </div>
              </CollapsibleSection>
            </>
          )}

          {/* NOTIFICATIONS TAB */}
          {activeTab === 'notifications' && (
            <CollapsibleSection title="Notifications" icon={Bell}>
              <div className="pt-2">
                <SettingRow icon={Bell} label="Push Notifications" description="Receive browser notifications">
                  <ToggleSwitch checked={formData.enableNotifications} onChange={(v) => handleChange('enableNotifications', v)} />
                </SettingRow>

                <SettingRow icon={Volume2} label="Notification Sound" description="Play sound for notifications">
                  <ToggleSwitch 
                    checked={formData.notificationSound} 
                    onChange={(v) => handleChange('notificationSound', v)}
                    disabled={!formData.enableNotifications}
                  />
                </SettingRow>

                <SettingRow icon={Mail} label="Email Reminders" description="Receive email reminders for meetings">
                  <ToggleSwitch checked={formData.emailReminders} onChange={(v) => handleChange('emailReminders', v)} />
                </SettingRow>

                <SettingRow icon={Clock} label="Reminder Time" description="Minutes before meeting to remind">
                  <select 
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={formData.reminderTime}
                    onChange={(e) => handleChange('reminderTime', parseInt(e.target.value))}
                    disabled={!formData.enableNotifications && !formData.emailReminders}
                  >
                    <option value={5}>5 minutes</option>
                    <option value={10}>10 minutes</option>
                    <option value={15}>15 minutes</option>
                    <option value={30}>30 minutes</option>
                    <option value={60}>1 hour</option>
                    <option value={1440}>1 day</option>
                  </select>
                </SettingRow>
              </div>
            </CollapsibleSection>
          )}

          {/* AI FEATURES TAB */}
          {activeTab === 'ai' && (
            <>
              <CollapsibleSection title="AI Features" icon={Sparkles} badge="Beta">
                <div className="pt-2">
                  <SettingRow icon={Sparkles} label="Enable AI Features" description="Use AI for summaries and suggestions">
                    <ToggleSwitch checked={formData.enableAI} onChange={(v) => handleChange('enableAI', v)} />
                  </SettingRow>

                  {formData.enableAI && (
                    <>
                      <SettingRow icon={Key} label="AI Provider" description="Choose your AI provider">
                        <select 
                          className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          value={formData.aiProvider}
                          onChange={(e) => handleChange('aiProvider', e.target.value)}
                        >
                          <option value="gemini">Google Gemini</option>
                          <option value="openai">OpenAI</option>
                          <option value="claude">Anthropic Claude</option>
                        </select>
                      </SettingRow>

                      <SettingRow icon={Key} label="API Key" description="Your API key (stored locally)">
                        <div className="flex items-center gap-2">
                          <input 
                            type={showApiKey ? 'text' : 'password'}
                            value={formData.aiApiKey}
                            onChange={(e) => handleChange('aiApiKey', e.target.value)}
                            placeholder="Enter API key"
                            className="w-48 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                          <button 
                            onClick={() => setShowApiKey(!showApiKey)}
                            className="p-2 text-gray-400 hover:text-gray-600"
                          >
                            {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </SettingRow>

                      <SettingRow icon={Mic} label="Auto Transcription" description="Automatically transcribe meeting recordings">
                        <ToggleSwitch checked={formData.enableTranscription} onChange={(v) => handleChange('enableTranscription', v)} />
                      </SettingRow>

                      <SettingRow icon={FileText} label="Auto Summaries" description="Generate meeting summaries automatically">
                        <ToggleSwitch checked={formData.enableSummaries} onChange={(v) => handleChange('enableSummaries', v)} />
                      </SettingRow>
                    </>
                  )}
                </div>
              </CollapsibleSection>

              {/* Integrations */}
              <CollapsibleSection title="Integrations" icon={Zap} defaultOpen={false}>
                <div className="pt-2">
                  <SettingRow icon={Mic} label="Voice Recording" description="Enable voice notes and recording">
                    <ToggleSwitch checked={formData.enableVoiceRecording} onChange={(v) => handleChange('enableVoiceRecording', v)} />
                  </SettingRow>

                  <SettingRow icon={MapPin} label="Location Services" description="Use location for meeting suggestions">
                    <ToggleSwitch checked={formData.enableLocationServices} onChange={(v) => handleChange('enableLocationServices', v)} />
                  </SettingRow>
                </div>
              </CollapsibleSection>
            </>
          )}

          {/* DATA TAB */}
          {activeTab === 'data' && (
            <>
              {/* Data Management */}
              <CollapsibleSection title="Data Management" icon={Database}>
                <div className="pt-4 space-y-4">
                  <div className="flex gap-3">
                    <Button variant="secondary" onClick={handleExport} className="flex-1">
                      <Download className="w-4 h-4 mr-2" /> Export Data
                    </Button>
                    <Button variant="secondary" onClick={onImportData} className="flex-1">
                      <Upload className="w-4 h-4 mr-2" /> Import Data
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">Export all your meetings, contacts, and journal entries as a JSON file.</p>
                </div>
              </CollapsibleSection>

              {/* Danger Zone */}
              <Card className="border-red-200 bg-red-50/30">
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-red-800 mb-2 flex items-center">
                    <AlertTriangle className="w-5 h-5 mr-2" />
                    Danger Zone
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Resetting data will permanently delete all meetings, contacts, and journal entries. This action cannot be undone.
                  </p>
                  <Button 
                    variant="danger" 
                    onClick={() => {
                      if (window.confirm('Are you sure you want to reset all data? This cannot be undone.')) {
                        onResetData?.();
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Reset All Data
                  </Button>
                </div>
              </Card>

              {/* Keyboard Shortcuts */}
              <Card className="bg-gray-50">
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                    <Keyboard className="w-4 h-4 mr-2" />
                    Keyboard Shortcuts
                  </h3>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex justify-between"><span className="text-gray-500">New Meeting</span><kbd className="px-2 py-0.5 bg-white rounded border">N</kbd></div>
                    <div className="flex justify-between"><span className="text-gray-500">Quick Search</span><kbd className="px-2 py-0.5 bg-white rounded border">⌘ K</kbd></div>
                    <div className="flex justify-between"><span className="text-gray-500">Today</span><kbd className="px-2 py-0.5 bg-white rounded border">T</kbd></div>
                    <div className="flex justify-between"><span className="text-gray-500">Settings</span><kbd className="px-2 py-0.5 bg-white rounded border">,</kbd></div>
                  </div>
                </div>
              </Card>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default DiarySettingsView;